import axios from "axios";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { castDraft } from "immer";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";

import logger from "../../../art-logger-config";
import { searchAggregations } from "../../app/api";
import { RootState } from "../../app/rootReducer";
import {
  AggregationFacet,
  SearchAggregationResult,
} from "../../../from-server/asin-central/search";
import {
  postProgramTicketDetails,
  postProgramTickets,
} from "./programTicketsApi";
import { OPEN_ONLY_WIRE_STATUSES, rollupStatusOf } from "./rollupStatus";
import {
  GetProgramTicketDetailsResponse,
  GetProgramTicketsResponse,
  ProgramTicket,
  ProgramTicketDetails,
} from "./types";

// ---- constants ----

const DEFAULT_PAGE_SIZE = 10;
const RESPONSE_CACHE_LIMIT = 8;
// DACS registers the program concept id under `programId`; `program`
// is not an aggregatable attribute.
const PROGRAM_ATTRIBUTE = "programId";
const PROGRAM_AGGREGATION_TOTAL = 200;
// Tickety's SearchTickets caps `filters.tags.or` at 20 entries. DACS
// maps one program concept id to one tag, so the client can safely
// send at most this many ids per /tickets request. When the aggregation
// yields more, we truncate to the largest programs by documentCount
// and surface the truncation count on state so the header can render
// a "showing 20 of N" hint.
export const MAX_PROGRAM_CONCEPT_IDS_PER_REQUEST = 20;

// ---- types ----

export type ProgramTicketsStatus = "idle" | "loading" | "success" | "error";

export type ProgramTicketDetailsStatus =
  | "idle"
  | "loading"
  | "success"
  | "error";

export interface CachedProgramTicketsResponse {
  readonly etag: string;
  readonly response: GetProgramTicketsResponse;
}

export interface RollupCounts {
  readonly open: number;
  readonly wip: number;
  readonly resolved: number;
}

/**
 * Set when the aggregation returned more program buckets than the
 * per-request cap allows. `shown` is the number of program concept ids
 * actually forwarded to `/tickets`, `total` is the aggregation's full
 * bucket count. `undefined` when no truncation applied on the latest
 * fetch.
 */
export interface ProgramTruncation {
  readonly shown: number;
  readonly total: number;
}

export interface ProgramTicketsState {
  status: ProgramTicketsStatus;
  list: readonly ProgramTicket[];
  totalCount: number;
  page: number;
  pageSize: number;
  error: string | undefined;
  lastFetchedKey: string | undefined;
  lastFetchedSearchQueryString: string | undefined;
  responseCache: Record<string, CachedProgramTicketsResponse>;
  responseCacheOrder: readonly string[];
  focusedTicketId: string | undefined;
  selectedTicketIds: readonly string[];
  detailsById: Record<string, ProgramTicketDetails>;
  detailsStatus: Record<string, ProgramTicketDetailsStatus>;
  detailsError: Record<string, string | undefined>;
  textFilter: string;
  openOnly: boolean;
  programTruncation: ProgramTruncation | undefined;
}

const initialState: ProgramTicketsState = {
  status: "idle",
  list: [],
  totalCount: 0,
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  error: undefined,
  lastFetchedKey: undefined,
  lastFetchedSearchQueryString: undefined,
  responseCache: {},
  responseCacheOrder: [],
  focusedTicketId: undefined,
  selectedTicketIds: [],
  detailsById: {},
  detailsStatus: {},
  detailsError: {},
  textFilter: "",
  openOnly: false,
  programTruncation: undefined,
};

// ---- canonical key ----

export function canonicalRequestKey(
  programConceptIds: readonly string[],
  page: number,
  openOnly: boolean,
  textFilter: string
): string {
  const sortedConcepts = [...programConceptIds].sort().join(",");
  const openOnlyKey = openOnly ? "1" : "0";
  const normalizedQuery = textFilter.trim().toLowerCase();
  return (
    "c=" +
    sortedConcepts +
    "|p=" +
    page +
    "|o=" +
    openOnlyKey +
    "|k=" +
    normalizedQuery
  );
}

function isZeroStateQuery(searchQueryString: string): boolean {
  return searchQueryString === "" || searchQueryString === "(all)";
}

// ---- in-flight tracking ----

const inflightAggregationControllers = new Set<AbortController>();
const inflightListControllers = new Set<AbortController>();
const inflightDetailsControllers = new Set<AbortController>();

const inflightListRequests = new Map<
  string,
  Promise<GetProgramTicketsResponse | undefined>
>();

// ---- abort helpers ----

export function isAbortError(err: unknown): boolean {
  // axios 0.25 rejects aborted requests with a `Cancel` value that only
  // `axios.isCancel` reliably recognizes — `AbortError` name and the
  // `ERR_CANCELED` code came later. The raw-`AbortError` and code paths
  // stay so a bare AbortController rejection (e.g. from a non-axios
  // caller) is still recognized.
  if (axios.isCancel(err)) return true;
  if (err instanceof Error && err.name === "AbortError") return true;
  const withCode = err as { code?: string } | undefined;
  return withCode?.code === "ERR_CANCELED";
}

function createLinkedController(
  callerSignal: AbortSignal | undefined,
  registry: Set<AbortController>
): { controller: AbortController; dispose: () => void } {
  const controller = new AbortController();
  let onCallerAbort: (() => void) | undefined;
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      onCallerAbort = () => controller.abort();
      callerSignal.addEventListener("abort", onCallerAbort, { once: true });
    }
  }
  registry.add(controller);
  return {
    controller,
    dispose: () => {
      registry.delete(controller);
      if (callerSignal && onCallerAbort) {
        callerSignal.removeEventListener("abort", onCallerAbort);
      }
    },
  };
}

// ---- aggregation ----

/**
 * Runs a `terms` aggregation on the `programId` attribute over the
 * current ASIN match set and returns the distinct set of program
 * concept id bucket keys, ordered by `documentCount` descending. The
 * ordering matters because the caller truncates to
 * `MAX_PROGRAM_CONCEPT_IDS_PER_REQUEST` before dispatching `/tickets`
 * — sorting by documentCount desc means the largest (most-populated)
 * programs are kept when truncation applies.
 */
export async function runProgramFacetAggregation(
  searchQueryString: string,
  signal: AbortSignal
): Promise<readonly string[]> {
  const facets: AggregationFacet[] = [
    {
      attributeName: PROGRAM_ATTRIBUTE,
      aggregationType: "terms",
      totalAggregationCount: PROGRAM_AGGREGATION_TOTAL,
    },
  ];
  // Forward the signal directly to axios so an aborted aggregation
  // releases its network slot immediately — the previous raceWithSignal
  // wrapper only abandoned the wait, letting the underlying HTTP call
  // keep running until DACS answered.
  const result: SearchAggregationResult = await searchAggregations(
    searchQueryString,
    facets,
    undefined,
    { signal }
  );
  const buckets = result.aggregationResults?.[PROGRAM_ATTRIBUTE] ?? [];
  // Sort by documentCount desc; tiebreak by key for stable ordering
  // across identical aggregations.
  const sorted = [...buckets].sort((a, b) => {
    if (a.documentCount !== b.documentCount) {
      return b.documentCount - a.documentCount;
    }
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });
  return sorted.map((bucket) => bucket.key);
}

// ---- slice ----

interface ListSuccessPayload {
  readonly key: string;
  readonly response: GetProgramTicketsResponse;
  readonly etag: string | undefined;
}

interface ListFailurePayload {
  readonly key: string | undefined;
  readonly error: string;
}

interface DetailsSuccessPayload {
  readonly ticketId: string;
  readonly details: ProgramTicketDetails;
}

interface DetailsFailurePayload {
  readonly ticketId: string;
  readonly error: string;
}

interface SelectionPayload {
  readonly selectedTicketIds: readonly string[];
  readonly focusedTicketId: string | undefined;
}

function evictResponseCacheIfNeeded(state: ProgramTicketsState): void {
  while (state.responseCacheOrder.length > RESPONSE_CACHE_LIMIT) {
    const oldestKey = state.responseCacheOrder[0];
    state.responseCacheOrder = state.responseCacheOrder.slice(1);
    delete state.responseCache[oldestKey];
  }
}

const programTicketsSlice = createSlice({
  name: "programTickets",
  initialState,
  reducers: {
    programTicketsListStart(
      state,
      { payload }: PayloadAction<{ key: string; searchQueryString: string }>
    ) {
      // Fresh filter: wipe the visible rows so the table falls into
      // its empty + loading state ("Loading program tickets...")
      // instead of leaving rows from the previous filter's programs
      // in place while the new fetch runs. The response cache is
      // preserved so a subsequent same-key fetch still short-circuits
      // through the 304 path. Selection is cleared too so the details
      // panel and selected-count don't reference rows the user can no
      // longer see while the new fetch is in flight.
      if (payload.searchQueryString !== state.lastFetchedSearchQueryString) {
        state.list = [];
        state.totalCount = 0;
        state.programTruncation = undefined;
        state.selectedTicketIds = [];
        state.focusedTicketId = undefined;
      }
      state.status = "loading";
      state.error = undefined;
      state.lastFetchedKey = payload.key;
      state.lastFetchedSearchQueryString = payload.searchQueryString;
    },
    programTicketsListSuccess(
      state,
      { payload }: PayloadAction<ListSuccessPayload>
    ) {
      state.status = "success";
      state.error = undefined;
      state.list = castDraft(payload.response.tickets);
      state.totalCount = payload.response.totalCount;
      state.lastFetchedKey = payload.key;
      // Prune selection to tickets still present in the new page. When
      // Open_Only or the text filter changes, the server may return a
      // page that no longer contains previously-selected tickets;
      // selection state should not point at rows the user can't see.
      const visibleIds = new Set(
        payload.response.tickets.map((t) => t.ticketId)
      );
      state.selectedTicketIds = state.selectedTicketIds.filter((id) =>
        visibleIds.has(id)
      );
      if (
        state.focusedTicketId !== undefined &&
        !visibleIds.has(state.focusedTicketId)
      ) {
        state.focusedTicketId =
          state.selectedTicketIds[state.selectedTicketIds.length - 1];
      }
      if (payload.etag !== undefined) {
        state.responseCache[payload.key] = castDraft({
          etag: payload.etag,
          response: payload.response,
        });
        state.responseCacheOrder = [
          ...state.responseCacheOrder.filter((k) => k !== payload.key),
          payload.key,
        ];
        evictResponseCacheIfNeeded(state);
      }
    },
    programTicketsListNotModified(
      state,
      { payload }: PayloadAction<{ key: string }>
    ) {
      state.status = "success";
      state.error = undefined;
      state.lastFetchedKey = payload.key;
      // Restore the cached response so the table renders the page that
      // matches the returning key, not the last successful page's rows.
      // The connector only sends If-None-Match when the cache holds an
      // entry for this key, so the cached response is authoritative here.
      const cached = state.responseCache[payload.key];
      if (cached !== undefined) {
        state.list = castDraft(cached.response.tickets);
        state.totalCount = cached.response.totalCount;
        const visibleIds = new Set(
          cached.response.tickets.map((t) => t.ticketId)
        );
        state.selectedTicketIds = state.selectedTicketIds.filter((id) =>
          visibleIds.has(id)
        );
        if (
          state.focusedTicketId !== undefined &&
          !visibleIds.has(state.focusedTicketId)
        ) {
          state.focusedTicketId =
            state.selectedTicketIds[state.selectedTicketIds.length - 1];
        }
      }
      state.responseCacheOrder = [
        ...state.responseCacheOrder.filter((k) => k !== payload.key),
        payload.key,
      ];
    },
    programTicketsListFailure(
      state,
      { payload }: PayloadAction<ListFailurePayload>
    ) {
      state.status = "error";
      state.error = payload.error;
      if (payload.key !== undefined) {
        state.lastFetchedKey = payload.key;
      }
    },
    programTicketsListZeroState(state) {
      state.status = "success";
      state.error = undefined;
      state.list = [];
      state.totalCount = 0;
      state.lastFetchedKey = undefined;
      state.programTruncation = undefined;
    },
    setProgramTicketsTruncation(
      state,
      { payload }: PayloadAction<ProgramTruncation | undefined>
    ) {
      state.programTruncation = payload;
    },
    programTicketDetailsStart(
      state,
      { payload }: PayloadAction<{ ticketId: string }>
    ) {
      state.detailsStatus[payload.ticketId] = "loading";
      state.detailsError[payload.ticketId] = undefined;
    },
    programTicketDetailsSuccess(
      state,
      { payload }: PayloadAction<DetailsSuccessPayload>
    ) {
      state.detailsStatus[payload.ticketId] = "success";
      state.detailsError[payload.ticketId] = undefined;
      state.detailsById[payload.ticketId] = castDraft(payload.details);
    },
    programTicketDetailsFailure(
      state,
      { payload }: PayloadAction<DetailsFailurePayload>
    ) {
      state.detailsStatus[payload.ticketId] = "error";
      state.detailsError[payload.ticketId] = payload.error;
    },
    setProgramTicketsTextFilter(state, { payload }: PayloadAction<string>) {
      state.textFilter = payload;
      // Server applies the text filter on the next fetch. Reset to page 0
      // so the user sees the first page of the new filtered result set.
      state.page = 0;
    },
    setProgramTicketsOpenOnly(state, { payload }: PayloadAction<boolean>) {
      state.openOnly = payload;
      state.page = 0;
      // The toggle is a single-click intent that triggers an
      // immediate refetch via `applyOpenOnlyFilter`. Surface the
      // loading state right away and wipe the current rows so the
      // table renders its "Loading program tickets…" spinner during
      // the aggregation call, rather than sitting on the previous
      // page's rows until the wire response returns.
      state.status = "loading";
      state.list = [];
      state.totalCount = 0;
      state.programTruncation = undefined;
      state.selectedTicketIds = [];
      state.focusedTicketId = undefined;
      state.error = undefined;
    },
    setProgramTicketsPage(state, { payload }: PayloadAction<number>) {
      state.page = payload;
      state.selectedTicketIds = [];
      state.focusedTicketId = undefined;
    },
    setProgramTicketsSelection(
      state,
      { payload }: PayloadAction<SelectionPayload>
    ) {
      state.selectedTicketIds = castDraft(payload.selectedTicketIds);
      state.focusedTicketId = payload.focusedTicketId;
    },
  },
});

export const {
  programTicketsListStart,
  programTicketsListSuccess,
  programTicketsListNotModified,
  programTicketsListFailure,
  programTicketsListZeroState,
  programTicketDetailsStart,
  programTicketDetailsSuccess,
  programTicketDetailsFailure,
  setProgramTicketsTextFilter,
  setProgramTicketsOpenOnly,
  setProgramTicketsPage,
  setProgramTicketsSelection,
  setProgramTicketsTruncation,
} = programTicketsSlice.actions;

export default programTicketsSlice.reducer;

// ---- thunks ----

type ProgramTicketsThunk<R = void> = ThunkAction<
  Promise<R>,
  RootState,
  unknown,
  AnyAction
>;

export interface FetchProgramTicketsArg {
  readonly searchQueryString: string;
  readonly page: number;
  readonly signal?: AbortSignal;
}

export const fetchProgramTickets =
  ({
    searchQueryString,
    page,
    signal,
  }: FetchProgramTicketsArg): ProgramTicketsThunk<GetProgramTicketsResponse> =>
  async (dispatch, getState) => {
    if (isZeroStateQuery(searchQueryString)) {
      dispatch(programTicketsListZeroState());
      return { tickets: [], totalCount: 0 };
    }

    const { controller: aggController, dispose: disposeAgg } =
      createLinkedController(signal, inflightAggregationControllers);

    let conceptIds: readonly string[];
    try {
      conceptIds = await runProgramFacetAggregation(
        searchQueryString,
        aggController.signal
      );
    } catch (err: unknown) {
      disposeAgg();
      if (isAbortError(err)) {
        return { tickets: [], totalCount: 0 };
      }
      const message = err instanceof Error ? err.message : String(err);
      logger.error(`Program facet aggregation failed: ${message}`);
      dispatch(programTicketsListFailure({ key: undefined, error: message }));
      throw err;
    }
    disposeAgg();

    if (conceptIds.length === 0) {
      dispatch(programTicketsListZeroState());
      return { tickets: [], totalCount: 0 };
    }

    // Cap forwarded concept ids at the Tickety filters.tags.or limit.
    // runProgramFacetAggregation returns buckets in documentCount desc
    // order, so slicing the head keeps the largest programs.
    const totalConceptIdCount = conceptIds.length;
    const truncated = totalConceptIdCount > MAX_PROGRAM_CONCEPT_IDS_PER_REQUEST;
    const forwardedConceptIds = truncated
      ? conceptIds.slice(0, MAX_PROGRAM_CONCEPT_IDS_PER_REQUEST)
      : conceptIds;

    const state = getState().programTickets;
    const openOnly = state.openOnly;
    const textFilter = state.textFilter;
    const statuses = openOnly ? OPEN_ONLY_WIRE_STATUSES : undefined;
    const trimmedQuery = textFilter.trim();
    const query = trimmedQuery.length > 0 ? trimmedQuery : undefined;

    const key = canonicalRequestKey(
      forwardedConceptIds,
      page,
      openOnly,
      textFilter
    );

    // Dispatch `programTicketsListStart` before `setProgramTicketsTruncation`
    // so a fresh-filter Start (which clears stale `programTruncation`)
    // doesn't erase the truncation we're about to publish for this
    // request.
    dispatch(programTicketsListStart({ key, searchQueryString }));
    dispatch(
      setProgramTicketsTruncation(
        truncated
          ? {
              shown: forwardedConceptIds.length,
              total: totalConceptIdCount,
            }
          : undefined
      )
    );

    const existing = inflightListRequests.get(key);
    if (existing !== undefined) {
      const response = await existing;
      return response ?? { tickets: [], totalCount: 0 };
    }

    const { controller: listController, dispose: disposeList } =
      createLinkedController(signal, inflightListControllers);

    const cached = state.responseCache[key];
    const ifNoneMatch = cached?.etag;

    const requestPromise = (async (): Promise<
      GetProgramTicketsResponse | undefined
    > => {
      try {
        const result = await postProgramTickets(
          {
            programConceptIds: forwardedConceptIds,
            statuses,
            query,
            page,
            pageSize: state.pageSize,
          },
          { signal: listController.signal, ifNoneMatch }
        );
        if (result.status === 304) {
          dispatch(programTicketsListNotModified({ key }));
          return cached?.response ?? { tickets: [], totalCount: 0 };
        }
        dispatch(
          programTicketsListSuccess({
            key,
            response: result.response,
            etag: result.etag,
          })
        );
        return result.response;
      } catch (err: unknown) {
        if (isAbortError(err)) {
          return undefined;
        }
        const message = err instanceof Error ? err.message : String(err);
        logger.error(`Program tickets request failed: ${message}`);
        dispatch(programTicketsListFailure({ key, error: message }));
        throw err;
      } finally {
        inflightListRequests.delete(key);
        disposeList();
      }
    })();

    inflightListRequests.set(key, requestPromise);
    const finalResponse = await requestPromise;
    return finalResponse ?? { tickets: [], totalCount: 0 };
  };

export interface FetchProgramTicketDetailsArg {
  readonly ticketId: string;
  readonly signal?: AbortSignal;
}

export const fetchProgramTicketDetails =
  ({
    ticketId,
    signal,
  }: FetchProgramTicketDetailsArg): ProgramTicketsThunk<
    GetProgramTicketDetailsResponse | undefined
  > =>
  async (dispatch) => {
    dispatch(programTicketDetailsStart({ ticketId }));

    const { controller, dispose } = createLinkedController(
      signal,
      inflightDetailsControllers
    );

    try {
      const details = await postProgramTicketDetails(
        { ticketId },
        { signal: controller.signal }
      );
      dispatch(
        programTicketDetailsSuccess({
          ticketId,
          details,
        })
      );
      return details;
    } catch (err: unknown) {
      if (isAbortError(err)) {
        return undefined;
      }
      const message = err instanceof Error ? err.message : String(err);
      logger.error(
        `Program ticket details request for ${ticketId} failed: ${message}`
      );
      dispatch(programTicketDetailsFailure({ ticketId, error: message }));
      return undefined;
    } finally {
      dispose();
    }
  };

/**
 * Aborts every in-flight aggregation, list, and details controller
 * owned by this connector. Callers dispatch this on tab unmount and on
 * any lifecycle change that must halt outstanding work.
 */
export const cancelAllProgramTicketsRequests =
  (): ProgramTicketsThunk => async () => {
    filterFetchController?.abort();
    filterFetchController = undefined;
    abortAllControllersInRegistry(inflightAggregationControllers);
    abortAllControllersInRegistry(inflightListControllers);
    abortAllControllersInRegistry(inflightDetailsControllers);
    inflightListRequests.clear();
  };

/**
 * Shared abort controller for filter-driven fetches. Toggle and text
 * changes replace this; the previous controller's abort propagates
 * through `fetchProgramTickets`'s linked-controller chain to cancel
 * both the aggregation call and the `/tickets` HTTP request that
 * were still in flight from the prior filter change.
 */
let filterFetchController: AbortController | undefined;

function beginFilterFetch(): AbortSignal {
  filterFetchController?.abort();
  const next = new AbortController();
  filterFetchController = next;
  return next.signal;
}

/**
 * Commits an Open_Only_Toggle change and re-runs the pipe. The reducer
 * resets page to 0; the fetch reads the new `openOnly` flag out of
 * state and forwards the derived `statuses` list to the wire request.
 */
export const applyOpenOnlyFilter =
  (next: boolean, searchQueryString: string): ProgramTicketsThunk =>
  async (dispatch) => {
    const signal = beginFilterFetch();
    dispatch(setProgramTicketsOpenOnly(next));
    await dispatch(fetchProgramTickets({ searchQueryString, page: 0, signal }));
  };

/**
 * Commits a Program_Ticket_Text_Filter change and re-runs the pipe.
 * Callers are responsible for debouncing keystrokes before dispatching.
 * The reducer resets page to 0; the fetch reads the new `textFilter`
 * out of state and forwards the trimmed value as `query` on the wire.
 */
export const applyTextFilter =
  (next: string, searchQueryString: string): ProgramTicketsThunk =>
  async (dispatch) => {
    const signal = beginFilterFetch();
    dispatch(setProgramTicketsTextFilter(next));
    await dispatch(fetchProgramTickets({ searchQueryString, page: 0, signal }));
  };

function abortAllControllersInRegistry(registry: Set<AbortController>): void {
  const snapshot = Array.from(registry);
  registry.clear();
  for (const controller of snapshot) {
    controller.abort();
  }
}

// Exposed for tests so state can be reset between test cases.
export function __resetProgramTicketsInflightForTests(): void {
  inflightAggregationControllers.clear();
  inflightListControllers.clear();
  inflightDetailsControllers.clear();
  inflightListRequests.clear();
}

export function __peekInflightControllersForTests(): {
  readonly aggregation: ReadonlySet<AbortController>;
  readonly list: ReadonlySet<AbortController>;
  readonly details: ReadonlySet<AbortController>;
} {
  return {
    aggregation: inflightAggregationControllers,
    list: inflightListControllers,
    details: inflightDetailsControllers,
  };
}

// ---- selectors ----

const selectList = (state: RootState): readonly ProgramTicket[] =>
  state.programTickets.list;

// Server applies the Open_Only and text-filter reductions before
// returning; the client just displays the page verbatim. `textFilter` and
// `openOnly` stay in state so the header component can echo them into the
// wire request via the fetch thunk.
export const selectVisibleRows = createSelector(
  [selectList],
  (list): readonly ProgramTicket[] => list
);

export const selectRollupCounts = createSelector(
  [selectVisibleRows],
  (visibleRows): RollupCounts => {
    let open = 0;
    let wip = 0;
    let resolved = 0;
    for (const row of visibleRows) {
      switch (rollupStatusOf(row.status)) {
        case "open":
          open += 1;
          break;
        case "work-in-progress":
          wip += 1;
          break;
        case "resolved":
          resolved += 1;
          break;
      }
    }
    return { open, wip, resolved };
  }
);

export const selectDistinctPrograms = createSelector(
  [selectList],
  (list): readonly string[] => {
    const set = new Set<string>();
    for (const row of list) {
      set.add(row.programName);
    }
    return Array.from(set).sort();
  }
);

export const selectProgramTruncation = (
  state: RootState
): ProgramTruncation | undefined => state.programTickets.programTruncation;
